import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { Card, Tabs } from "antd";
import firebase from "../../../../configs/FirebaseConfig";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import {
  PeriodSelector,
  ProductDivisionPieChart,
  ProductSalesDetailsTable,
  PurchaseSuggestionsTable,
  SalesTrendsChart,
  Top10ProductsByAmount,
  Top10ProductsByRevenue,
} from "./components";

const db = firebase.firestore();

const ProductSalesDashboard = () => {
  const { t } = useTranslation();
  const [period, setPeriod] = useState("year");
  const [salesData, setSalesData] = useState([]);
  const [periodSalesData, setPeriodSalesData] = useState([]);
  const [processedProducts, setProcessedProducts] = useState([]);
  const [topProductsByAmount, setTopProductsByAmount] = useState([]);
  const [topProductsByRevenue, setTopProductsByRevenue] = useState([]);
  const [trendData, setTrendData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [activeTab, setActiveTab] = useState("overview");
  const [restockSuggestions, setRestockSuggestions] = useState([]);

  useEffect(() => {
    if (!salesData.length) fetchInitialData();
    else {
      const startUnix = !!startDate ? dayjs(startDate).unix() : getStartDateForPeriod(period);
      const endUnix = !!endDate ? dayjs(endDate).unix() : dayjs().subtract(1, "day").endOf("day").unix();
      updateSalesData(startUnix, endUnix);
    }
  }, [period, startDate, endDate]);

  const fetchInitialData = async () => {
    try {
      // Haal in eerste instantie de data voor een jaar op
      const yearStartDate = dayjs().subtract(1, "year").startOf("day").unix();
      const yearEndDate = dayjs().subtract(1, "day").endOf("day").unix();

      const salesRef = db.collection("salesAggregates");
      const snapshot = await salesRef
        .where("dateUnix", ">=", yearStartDate)
        .where("dateUnix", "<=", yearEndDate)
        .orderBy("dateUnix")
        .get();

      if (snapshot.empty) {
        setSalesData([]);
        setProcessedProducts([]);
        setTopProductsByAmount([]);
        setTopProductsByRevenue([]);
        setTrendData([]);
        setRestockSuggestions([]);
        setIsLoading(false);
        return;
      }

      const data = snapshot.docs.map((doc) => doc.data());

      // Verwerk de data
      setSalesData(data);
      setPeriodSalesData(data);
      processPeriodData(data);
    } catch (error) {
      console.error("Error fetching sales data:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const getSalesInPeriod = (start, end) => {
    return salesData.filter((sale) => sale.dateUnix >= start && sale.dateUnix <= end);
  };

  const updateSalesData = async (startUnix, endUnix) => {
    setIsLoading(true);

    try {
      const dataInPeriod = getSalesInPeriod(startUnix, endUnix);

      // Verwerk de data
      setPeriodSalesData(dataInPeriod);
      processPeriodData(dataInPeriod);
    } catch (error) {
      console.error("Error fetching sales data:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const processPeriodData = (salesData) => {
    const processed = processProductSalesData(salesData);
    setProcessedProducts(processed);
    setTopProductsByAmount(processed.slice(0, 10));

    // Bereken top 10 producten op basis van omzet
    const sortedByRevenue = processed.sort((a, b) => b.totalValue - a.totalValue);
    setTopProductsByRevenue(sortedByRevenue.slice(0, 10));

    // Bereken aanvullingssuggesties
    calculateRestockSuggestions(processed);

    // Bereken trenddata voor de top 5 producten
    prepareTrendData(processed.slice(0, 5), salesData);
  };

  const getStartDateForPeriod = (selectedPeriod) => {
    const today = dayjs();
    let startDate;

    switch (selectedPeriod) {
      case "week":
        startDate = today.subtract(7, "day");
        break;
      case "month":
        startDate = today.subtract(1, "month");
        break;
      case "quarter":
        startDate = today.subtract(3, "month");
        break;
      case "halfyear":
        startDate = today.subtract(6, "month");
        break;
      case "year":
        startDate = today.subtract(1, "year");
        break;
      default:
        startDate = today.subtract(7, "day");
    }

    return startDate.unix();
  };

  const processProductSalesData = (salesData) => {
    // Maak een map om de verkopen per product bij te houden
    const productSales = {};

    salesData.forEach((day) => {
      Object.entries(day.products || {}).forEach(([id, product]) => {
        if (!productSales[id]) {
          productSales[id] = {
            id,
            name: product.name || "Onbekend product",
            totalQuantity: 0,
            totalValue: 0,
            dailyData: [],
            totalWeight: 0,
          };
        }

        productSales[id].totalQuantity += product.quantity || 0;
        productSales[id].totalValue += product.totalValue || 0;
        if (product.weight) {
          productSales[id].totalWeight += product.weight;
        }

        // Voeg dagelijkse data toe voor tijdreeksanalyse
        productSales[id].dailyData.push({
          date: day.date,
          quantity: product.quantity || 0,
          value: product.totalValue || 0,
        });
      });
    });

    // Converteer naar array en sorteer op verkochte hoeveelheid
    return Object.values(productSales).sort((a, b) => b.totalQuantity - a.totalQuantity);
  };

  /**
   * Berekent voorraadaanvulling suggesties op basis van historische verkopen
   * met een gewogen gemiddelde aanpak
   *
   * @param {Array} productData - Verwerkte productdata met dagelijkse verkopen
   * @param {number} daysToForecast - Aantal dagen om vooruit te voorspellen (standaard 30)
   * @returns {Array} - Array met aanvullingssuggesties
   */
  const calculateRestockSuggestions = (productData, daysToForecast = 30) => {
    // Groepeer dagelijkse data per week voor elk product
    const productsWithWeeklyData = productData.map((product) => {
      // Sorteer dagelijkse data op datum (oudste eerst)
      const sortedDailyData = [...product.dailyData].sort(
        (a, b) => dayjs(a.date, "DD-MM-YYYY").unix() - dayjs(b.date, "DD-MM-YYYY").unix(),
      );

      // Als er geen data is, return het product zonder wekelijkse data
      if (sortedDailyData.length === 0) {
        return { ...product, weeklyData: [] };
      }

      // Bepaal startdatum (eerste datum in de dataset)
      const firstDateStr = sortedDailyData[0].date;
      const startDate = dayjs(firstDateStr, "DD-MM-YYYY");

      // Groepeer data per week
      const weeklyData = [];
      let currentWeek = [];
      let currentWeekNumber = 0;

      sortedDailyData.forEach((day) => {
        // Bereken dagen sinds start en daaruit het weeknummer
        const dayDate = dayjs(day.date, "DD-MM-YYYY");
        const daysSinceStart = dayDate.diff(startDate, "day");
        const weekNumber = Math.floor(daysSinceStart / 7);

        if (weekNumber > currentWeekNumber && currentWeek.length > 0) {
          // Nieuwe week begonnen, sla vorige week op
          weeklyData.push({
            weekNumber: currentWeekNumber,
            startDate: currentWeek[0].date,
            endDate: currentWeek[currentWeek.length - 1].date,
            totalQuantity: currentWeek.reduce((sum, d) => sum + d.quantity, 0),
            daysInWeek: currentWeek.length,
          });

          currentWeek = [];
          currentWeekNumber = weekNumber;
        }

        // Voeg dag toe aan huidige week
        currentWeek.push(day);
      });

      // Voeg laatste week toe indien aanwezig
      if (currentWeek.length > 0) {
        weeklyData.push({
          weekNumber: currentWeekNumber,
          startDate: currentWeek[0].date,
          endDate: currentWeek[currentWeek.length - 1].date,
          totalQuantity: currentWeek.reduce((sum, d) => sum + d.quantity, 0),
          daysInWeek: currentWeek.length,
        });
      }

      return {
        ...product,
        weeklyData,
      };
    });

    // Bereken voorspelling met gewogen gemiddelde
    const suggestions = productsWithWeeklyData.map((product) => {
      // We hebben minstens één week aan data nodig
      if (!product.weeklyData || product.weeklyData.length === 0) {
        return {
          id: product.id,
          name: product.name,
          avgDailySales: product.totalQuantity / (product.dailyData?.length || 1),
          predictedSales: Math.ceil((product.totalQuantity / (product.dailyData?.length || 1)) * daysToForecast),
          confidence: "laag", // Weinig data beschikbaar
          restockSuggestion: Math.ceil((product.totalQuantity / (product.dailyData?.length || 1)) * daysToForecast),
        };
      }

      // Verdeel gewichten op basis van hoeveelheid beschikbare weken
      let weights;
      const weeksAvailable = product.weeklyData.length;

      if (weeksAvailable >= 3) {
        // Als we minstens 3 weken hebben, gebruiken we de voorgestelde gewichten
        weights = [0.5, 0.3, 0.2]; // Laatste week 50%, twee weken geleden 30%, drie weken geleden 20%
      } else if (weeksAvailable === 2) {
        weights = [0.7, 0.3]; // Laatste week 70%, twee weken geleden 30%
      } else {
        weights = [1]; // Alleen laatste week beschikbaar, 100% gewicht
      }

      // Haal de meest recente weken op (tot 3, afhankelijk van beschikbaarheid)
      const recentWeeks = product.weeklyData.slice(-Math.min(3, weeksAvailable));

      // Bereken gemiddelde dagelijkse verkoop per week (om te compenseren voor onvolledige weken)
      const weeklyDailyAverages = recentWeeks.map((week) => week.totalQuantity / week.daysInWeek);

      // Pas gewichten toe en bereken gewogen gemiddelde dagelijkse verkoop
      let weightedSum = 0;
      let weightSum = 0;

      weeklyDailyAverages.forEach((avgDailySales, index) => {
        if (index < weights.length) {
          weightedSum += avgDailySales * weights[index];
          weightSum += weights[index];
        }
      });

      // Gemiddelde dagelijkse verkoop met gewogen methode
      const weightedAvgDailySales = weightedSum / weightSum;

      // Voorspel verkopen voor de opgegeven periode
      const predictedSales = Math.ceil(weightedAvgDailySales * daysToForecast);

      // Bepaal betrouwbaarheid van voorspelling
      let confidence = "gemiddeld";
      if (weeksAvailable >= 4) {
        confidence = "hoog";
      } else if (weeksAvailable <= 1) {
        confidence = "laag";
      }

      // Extra berekeningen voor inkoopsuggestie
      // Hier kun je extra logica toevoegen om voorraadniveaus mee te nemen
      // of om minimale bestelhoeveelheden te respecteren
      const restockSuggestion = predictedSales;

      return {
        id: product.id,
        name: product.name,
        avgDailySales: weightedAvgDailySales,
        predictedSales: predictedSales,
        confidence: confidence,
        weeklyData: recentWeeks.map((week, i) => ({
          weekNumber: week.weekNumber,
          weight: i < weights.length ? weights[i] : 0,
          totalQuantity: week.totalQuantity,
          daysInWeek: week.daysInWeek,
          dailyAverage: week.totalQuantity / week.daysInWeek,
        })),
        restockSuggestion: restockSuggestion,
      };
    });

    setRestockSuggestions(suggestions);
  };

  const prepareTrendData = (topFiveProducts, rawData) => {
    // Maak een set van alle beschikbare datums
    const dates = [];
    rawData.forEach((day) => dates.push(day.date));

    // Bouw trenddata op per datum
    const trends = dates.map((date) => {
      const dayData = { date };

      // Zoek de verkoopaantallen voor elk product op deze datum
      topFiveProducts.forEach((product) => {
        const dailyEntry = product.dailyData.find((d) => d.date === date);
        dayData[product.name] = dailyEntry ? dailyEntry.quantity : 0;
      });

      return dayData;
    });

    setTrendData(trends);
  };

  // const getFormattedStartAndEndDates = () => {
  //   const currentStartDate = !!startDate ? dayjs(startDate) : dayjs.unix(getStartDateForPeriod(period));
  //   const currentEndDate = !!endDate ? dayjs(endDate) : dayjs().subtract(1, "day").endOf("day");
  //   const formattedStartDate = currentStartDate.format(DATE_FORMAT_DD_MM_YYYY);
  //   const formattedEndDate = currentEndDate.format(DATE_FORMAT_DD_MM_YYYY);
  //
  //   return formattedStartDate + " - " + formattedEndDate;
  // };

  // const exportItems = [
  //   {
  //     key: "1",
  //     label: (
  //       <ExcelExport
  //         data={
  //           processedProducts.length > 0
  //             ? getPeriodReportExcelExportData(processedProducts)
  //             : getPeriodReportExcelExportData([])
  //         }
  //         fileName={COMPANY_CONSTANTS.WEBSITE_NAME + " rapportage " + getFormattedStartAndEndDates()}
  //       >
  //         {t("excel")}
  //       </ExcelExport>
  //     ),
  //   },
  // ];

  return (
    <DashboardContainer>
      <Title>{t("product_sales_report")}</Title>

      <Card>
        <FlexContainer>
          <PeriodSelector
            period={period}
            setPeriod={setPeriod}
            startDate={startDate}
            setStartDate={setStartDate}
            endDate={endDate}
            setEndDate={setEndDate}
          />
          {/*<Dropdown menu={{ items: exportItems }}>*/}
          {/*  <Button className="ant-btn-primary">*/}
          {/*    <FileExcelOutlined /> {t("export")} <DownOutlined />*/}
          {/*  </Button>*/}
          {/*</Dropdown>*/}
        </FlexContainer>
      </Card>

      <Tabs
        activeKey={activeTab}
        onChange={(key) => setActiveTab(key)}
        style={{ marginTop: "1rem" }}
        items={[
          {
            key: "overview",
            label: t("overview"),
          },
          {
            key: "trends",
            label: t("trends"),
          },
          {
            key: "purchase_suggestions",
            label: t("purchase_suggestions"),
          },
        ]}
        forceRender
      />

      {activeTab === "overview" && (
        <>
          <Grid>
            <Top10ProductsByAmount topProductsByAmount={topProductsByAmount} />
            <Top10ProductsByRevenue topProductsByRevenue={topProductsByRevenue} />
          </Grid>

          <ProductDivisionPieChart topProductsByRevenue={topProductsByRevenue} processedProducts={processedProducts} />
        </>
      )}

      {activeTab === "trends" && <SalesTrendsChart trendData={trendData} topProductsByAmount={topProductsByAmount} />}

      {activeTab === "purchase_suggestions" && <PurchaseSuggestionsTable restockSuggestions={restockSuggestions} />}

      <ProductSalesDetailsTable processedProducts={processedProducts} />
    </DashboardContainer>
  );
};

const DashboardContainer = styled.div`
  padding: 1rem;
  background-color: #f9fafb;
  min-height: 100vh;
`;

const Title = styled.h1`
  font-size: 1.5rem;
  font-weight: 700;
  margin-bottom: 1.5rem;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 1.5rem;
  margin-bottom: 2rem;

  @media (min-width: 1024px) {
    grid-template-columns: 1fr 1fr;
  }
`;

const FlexContainer = styled.div`
  display: flex;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 1rem;
`;

export default ProductSalesDashboard;
